import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  IconButton,
  Box,
  Typography,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { DataService } from 'src/services/data-service';

interface LeaseEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  leaseId: string;
}

interface Tenant {
  id: string;
  fullName: string;
  emails: string[];
  phoneNumbers: string[];
}

interface Unit {
  id: string;
  number: string;
  rooms: number;
  bathrooms: number;
  areaM2: number;
}

interface LeaseType {
  id: string;
  name: string;
  description: string;
}

interface Currency {
  id: string;
  code: string;
  name: string;
}

interface FeeType {
  id: string;
  name: string;
  description: string;
}

const LeaseEditDialog: React.FC<LeaseEditDialogProps> = ({
  open,
  onClose,
  onSuccess,
  leaseId
}) => {
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dropdown data
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [leaseTypes, setLeaseTypes] = useState<LeaseType[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [feeTypes, setFeeTypes] = useState<FeeType[]>([]);

  // Form data
  const [formData, setFormData] = useState({
    id: '',
    unitId: '',
    tenantId: '',
    leaseTypeId: '',
    currencyId: '',
    startDate: '',
    endDate: '',
    paymentDateOn: 1,
    mainFeeTypeId: ''
  });

  useEffect(() => {
    if (open && leaseId) {
      loadAllData();
    }
  }, [open, leaseId]);

  const loadAllData = async () => {
    try {
      setFetchingData(true);

      // First, fetch all dropdown data
      const [tenantsRes, unitsRes, leaseTypesRes, currenciesRes, feeTypesRes] = await Promise.all([
        DataService.get('api/Tenant/all'),
        DataService.get('api/Unit/all'),
        DataService.get('api/lease-types/all'),
        DataService.get('api/Currency/all'),
        DataService.get('api/fee-types/all')
      ]);

      // Process and store dropdown data
      let loadedTenants: Tenant[] = [];
      let loadedUnits: Unit[] = [];
      let loadedLeaseTypes: LeaseType[] = [];
      let loadedCurrencies: Currency[] = [];
      let loadedFeeTypes: FeeType[] = [];

      if (tenantsRes.ok) {
        const data = await tenantsRes.json();
        loadedTenants = data.success ? data.data : (Array.isArray(data) ? data : []);
        setTenants(loadedTenants);
      }

      if (unitsRes.ok) {
        const data = await unitsRes.json();
        loadedUnits = data.success ? data.data : (Array.isArray(data) ? data : []);
        setUnits(loadedUnits);
      }

      if (leaseTypesRes.ok) {
        const data = await leaseTypesRes.json();
        loadedLeaseTypes = data.success ? data.data : (Array.isArray(data) ? data : []);
        setLeaseTypes(loadedLeaseTypes);
      }

      if (currenciesRes.ok) {
        const data = await currenciesRes.json();
        loadedCurrencies = data.success ? data.data : (Array.isArray(data) ? data : []);
        setCurrencies(loadedCurrencies);
      }

      if (feeTypesRes.ok) {
        const data = await feeTypesRes.json();
        loadedFeeTypes = data.success ? data.data : (Array.isArray(data) ? data : []);
        setFeeTypes(loadedFeeTypes);
      }

      // Now fetch lease data and populate form
      const leaseResponse = await DataService.get(`api/Lease/get/${leaseId}`);

      if (leaseResponse.ok) {
        const responseData = await leaseResponse.json();
        const leaseData = responseData.success ? responseData.data : responseData;

        console.log('Fetched lease data for edit:', leaseData);

        // Extract the date portion only (YYYY-MM-DD) for date inputs
        const formatDateForInput = (dateString: string) => {
          if (!dateString) return '';
          return dateString.split('T')[0];
        };

        // Use the IDs directly from the API response
        const leaseTypeId = leaseData.leaseTypeId || '';
        const currencyId = leaseData.currencyId || '';

        // For mainFeeType, find it from the fees array
        let mainFeeTypeId = '';
        if (leaseData.fees && leaseData.fees.length > 0) {
          const mainFee = leaseData.fees[0];
          // Try to find by name, as the fee might not have the type ID directly
          const feeTypeFromName = loadedFeeTypes.find(ft =>
            ft.name.toLowerCase() === mainFee.type?.toLowerCase()
          );
          mainFeeTypeId = feeTypeFromName?.id || '';
        }

        setFormData({
          id: leaseData.id,
          unitId: leaseData.unit?.id || '',
          tenantId: leaseData.tenant?.id || '',
          leaseTypeId: leaseTypeId,
          currencyId: currencyId,
          startDate: formatDateForInput(leaseData.startDate),
          endDate: formatDateForInput(leaseData.endDate),
          paymentDateOn: leaseData.paymentDateOn || 1,
          mainFeeTypeId: mainFeeTypeId
        });
      } else {
        throw new Error('Failed to fetch lease details');
      }

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load lease data');
    } finally {
      setFetchingData(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!formData.unitId || !formData.tenantId || !formData.leaseTypeId ||
        !formData.currencyId || !formData.startDate || !formData.endDate ||
        !formData.mainFeeTypeId) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        id: formData.id,
        unitId: formData.unitId,
        tenantId: formData.tenantId,
        leaseTypeId: formData.leaseTypeId,
        currencyId: formData.currencyId,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
        paymentDateOn: Number(formData.paymentDateOn),
        mainFeeTypeId: formData.mainFeeTypeId
      };

      console.log('Updating lease:', payload);

      const response = await DataService.post('api/Lease/update', payload);

      if (response.ok) {
        onSuccess();
        handleClose();
      } else {
        // Handle error response
        let errorMessage = `Failed to update lease (${response.status})`;
        try {
          const errorData = await response.json();
          console.error('API Error Response:', errorData);
          errorMessage = errorData.message || errorData.title || JSON.stringify(errorData);
        } catch (e) {
          // Response might not be JSON (404, 500, etc.)
          errorMessage = `API Error: ${response.status} ${response.statusText}`;
        }
        setError(errorMessage);
        console.error('Lease update failed:', errorMessage, 'Response:', response);
      }
    } catch (err) {
      console.error('Error updating lease:', err);
      setError('An error occurred while updating the lease');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      id: '',
      unitId: '',
      tenantId: '',
      leaseTypeId: '',
      currencyId: '',
      startDate: '',
      endDate: '',
      paymentDateOn: 1,
      mainFeeTypeId: ''
    });
    setError(null);
    onClose();
  };

  const selectedTenant = tenants.find((t) => t.id === formData.tenantId);
  const selectedUnit = units.find((u) => u.id === formData.unitId);

  if (fetchingData) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon color="primary" />
            <Typography variant="h5" fontWeight="bold">
              Edit Lease
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Tenant Selection */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" fontWeight="600" gutterBottom>
              Tenant Information
            </Typography>
            <Autocomplete<Tenant, false, false, false>
              options={tenants}
              getOptionLabel={(option) => option.fullName}
              value={selectedTenant || null}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleInputChange('tenantId', newValue.id);
                } else {
                  handleInputChange('tenantId', '');
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Tenant" required />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{option.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.emails[0] || 'No email'} • {option.phoneNumbers[0] || 'No phone'}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>

          {/* Unit Selection */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" fontWeight="600" gutterBottom>
              Unit Information
            </Typography>
            <Autocomplete<Unit, false, false, false>
              options={units}
              getOptionLabel={(option) => `Unit ${option.number}`}
              value={selectedUnit || null}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleInputChange('unitId', newValue.id);
                } else {
                  handleInputChange('unitId', '');
                }
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Unit" required />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">Unit {option.number}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.rooms} rooms • {option.bathrooms} baths • {option.areaM2} m²
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Lease Details */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" fontWeight="600" gutterBottom>
              Lease Details
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Lease Type</InputLabel>
              <Select
                value={formData.leaseTypeId}
                onChange={(e) => handleInputChange('leaseTypeId', e.target.value)}
                label="Lease Type"
              >
                {leaseTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currencyId}
                onChange={(e) => handleInputChange('currencyId', e.target.value)}
                label="Currency"
              >
                {currencies.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.code} - {currency.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.endDate}
              onChange={(e) => handleInputChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Payment Date (Day of Month)"
              type="number"
              value={formData.paymentDateOn}
              onChange={(e) => handleInputChange('paymentDateOn', parseInt(e.target.value))}
              inputProps={{ min: 1, max: 31 }}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Main Fee Type</InputLabel>
              <Select
                value={formData.mainFeeTypeId}
                onChange={(e) => handleInputChange('mainFeeTypeId', e.target.value)}
                label="Main Fee Type"
              >
                {feeTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Update Lease'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaseEditDialog;
